import {
  Alert,
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import type { DialogType, StyleObj } from '../../@types';
import { useGoldGamesCampaign } from '../../queries/goldGames';
import RetryAlert from '../molecules/RetryAlert';

type Props = DialogType & { marketplaceId: string; recordId: string };
const displayDate = (value?: string) => (value ? dayjs(value).format('DD/MM/YYYY HH:mm:ss') : 'Not recorded');

const styles: StyleObj = {
  list: { m: 0 },
  field: { mb: 1 },
  value: { m: 0, overflowWrap: 'anywhere' },
  awardCard: { border: 1, borderColor: 'divider', p: 2, borderRadius: 1 },
};

const CampaignContent = ({ query }: { query: ReturnType<typeof useGoldGamesCampaign> }) => {
  if (query.isLoading) return <CircularProgress aria-label='Loading saved campaign' />;
  if (query.isError) return <RetryAlert onRetry={query.refetch}>Unable to load saved campaign.</RetryAlert>;
  if (!query.data) return <Alert severity='info'>Saved campaign not found.</Alert>;
  const campaign = query.data;
  const awards = campaign.includedUserIds.map(
    (playerId) =>
      campaign.awards?.find((award) => award.playerId === playerId) ?? {
        playerId,
        playerName: playerId,
        status: campaign.awardedUserIds.includes(playerId) ? 'awarded' : 'pending',
        awardedAt: undefined,
        awardedByName: undefined,
        awardedBy: undefined,
      }
  );
  const fields = [
    ['Local ID', campaign._id],
    ['Provider campaign ID', campaign.campaignId ?? 'Not confirmed'],
    ['Campaign name', campaign.name ?? 'Not recorded'],
    ['Provider', campaign.providerName ?? 'Not recorded'],
    ['Game', campaign.gameName ?? campaign.settings.gameId],
    ['Quantity per player', campaign.settings.numberOfSpins],
    [
      'Amount per spin',
      campaign.betAmount == null ? 'Not recorded' : `${campaign.betAmount} ${campaign.settings.currency}`,
    ],
    ['Bet level', campaign.settings.betLevel],
    ['Coin value', campaign.settings.coinValue],
    ['From', displayDate(campaign.settings.campaignActivationDate)],
    ['To', displayDate(campaign.settings.campaignExpirationDate)],
    ['Date Added', displayDate(campaign.createdAt)],
    ['Added By', campaign.createdByName ?? campaign.createdBy],
    ['Status', campaign.status],
    ['Cancelled At', displayDate(campaign.cancelledAt)],
  ];
  return (
    <Stack spacing={2}>
      <Box component='dl' sx={styles.list}>
        {fields.map(([label, value]) => (
          <Box key={label} sx={styles.field}>
            <Typography component='dt' variant='caption'>
              {label}
            </Typography>
            <Typography component='dd' sx={styles.value}>
              {value}
            </Typography>
          </Box>
        ))}
      </Box>
      <Typography variant='h6'>Player awards</Typography>
      {awards.map((award) => (
        <Box key={award.playerId} sx={styles.awardCard}>
          <Typography>
            {award.playerName} ({award.playerId})
          </Typography>
          <Typography>Status: {award.status}</Typography>
          <Typography>Confirmed: {displayDate(award.awardedAt)}</Typography>
          <Typography>Awarded by: {award.awardedByName ?? award.awardedBy ?? 'Not recorded'}</Typography>
        </Box>
      ))}
    </Stack>
  );
};

const GoldGamesCampaignDetails = ({ marketplaceId, recordId, closeModal }: Props) => {
  const query = useGoldGamesCampaign(marketplaceId, recordId);
  return (
    <>
      <DialogTitle>Free Spins Campaign</DialogTitle>
      <DialogContent>
        <CampaignContent query={query} />
      </DialogContent>
      <DialogActions>
        <Button onClick={closeModal}>Close</Button>
      </DialogActions>
    </>
  );
};
export default GoldGamesCampaignDetails;
